/**
 * RFIDetailPage — Full RFI Portal ("ultra-detailed" view).
 *
 * Tabs:
 *  ▸ Overview     — subject, description, SLA aging, response
 *  ▸ Discussion   — threaded comments (public / internal), @mentions
 *  ▸ Impact       — cost, schedule days, disposition
 *  ▸ Distribution — CC list & watchers
 *  ▸ Links        — cross-links to tasks / drawings / submittals / documents
 *  ▸ Audit Trail  — immutable log
 *
 * Route: /project-controls/dms/:projectId/rfis/:rfiId
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, MessageSquare, TrendingDown, Users, Link2, ScrollText, Send,
  Lock, Globe, Clock, AlertTriangle, CheckCircle2, Download, Loader2, Sparkles, X,
} from 'lucide-react';
import api from '@/lib/api';
import DocumentPickerDialog from '@/components/dms/DocumentPickerDialog';

const surfacePanel = 'bg-white dark:bg-gradient-to-br dark:from-[#0c1222] dark:to-[#0a0f1e] border border-slate-200 dark:border-slate-700/50 rounded-xl';
const subPanel     = 'bg-slate-50 dark:bg-[#0a0f1e] border border-slate-200 dark:border-slate-700/40 rounded-lg';
const headTxt      = 'text-slate-800 dark:text-white';
const mutedTxt     = 'text-slate-500 dark:text-slate-400';
const bodyTxt      = 'text-slate-700 dark:text-slate-300';

const AGING_TONES = {
  RESPONDED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  OPEN:      'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
  'DUE SOON':'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  BREACHED:  'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
};

export default function RFIDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const projectId = params.projectId;
  const rfiId = params.rfiId;
  const [rfi, setRfi] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/edm/projects/${projectId}/rfis/${rfiId}`);
      setRfi(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load RFI');
    } finally { setLoading(false); }
  }, [projectId, rfiId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <DashboardLayout><div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading RFI…</div></DashboardLayout>;
  }
  if (!rfi) {
    return <DashboardLayout><div className="p-6 text-slate-500">RFI not found. <Button variant="link" onClick={() => navigate(-1)}>Go back</Button></div></DashboardLayout>;
  }

  const sla = rfi.sla || { aging_label: 'OPEN', aging_color: 'sky', open_days: 0 };
  const tone = AGING_TONES[sla.aging_label] || AGING_TONES.OPEN;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4" data-testid="rfi-detail-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-800 dark:hover:text-white flex items-center gap-1 mb-2" data-testid="rfi-back">
              <ArrowLeft className="w-3 h-3" /> Back to RFI list
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-sm text-slate-500">{rfi.rfi_number}</span>
              <h1 className={`text-2xl font-bold ${headTxt}`} data-testid="rfi-subject">{rfi.subject}</h1>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${tone}`} data-testid="rfi-aging">
                {sla.aging_label}
              </span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${rfi.priority === 'HIGH' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300' : rfi.priority === 'LOW' ? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'}`}>
                {rfi.priority}
              </span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">
                {rfi.discipline}
              </span>
            </div>
            <p className={`text-sm ${mutedTxt} mt-1`}>Created {fmtDate(rfi.created_at)} by <span className="font-mono">{rfi.created_by}</span></p>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI icon={Clock} label="Open days" value={sla.open_days ?? '—'} tone="sky" />
          <KPI icon={AlertTriangle} label="Days to Due" value={sla.days_to_due ?? '—'} tone={sla.days_to_due != null && sla.days_to_due < 0 ? 'red' : 'amber'} />
          <KPI icon={TrendingDown} label="Cost impact" value={`${fmtCurrency(rfi.cost_impact, rfi.cost_currency)}`} tone="fuchsia" />
          <KPI icon={Clock} label="Schedule impact" value={`${rfi.schedule_impact_days || 0} d`} tone="amber" />
          <KPI icon={CheckCircle2} label="Disposition" value={rfi.disposition || 'NONE'} tone="emerald" />
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100 dark:bg-[#0c1222] p-0.5">
            <TabsTrigger value="overview"     className="text-xs" data-testid="rfi-tab-overview"><ScrollText className="w-3.5 h-3.5 mr-1.5" /> Overview</TabsTrigger>
            <TabsTrigger value="discussion"   className="text-xs" data-testid="rfi-tab-discussion"><MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Discussion ({(rfi.comments || []).length})</TabsTrigger>
            <TabsTrigger value="impact"       className="text-xs" data-testid="rfi-tab-impact"><TrendingDown className="w-3.5 h-3.5 mr-1.5" /> Impact</TabsTrigger>
            <TabsTrigger value="distribution" className="text-xs" data-testid="rfi-tab-distribution"><Users className="w-3.5 h-3.5 mr-1.5" /> Distribution</TabsTrigger>
            <TabsTrigger value="links"        className="text-xs" data-testid="rfi-tab-links"><Link2 className="w-3.5 h-3.5 mr-1.5" /> Links</TabsTrigger>
            <TabsTrigger value="audit"        className="text-xs" data-testid="rfi-tab-audit"><ScrollText className="w-3.5 h-3.5 mr-1.5" /> Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"     className="mt-3"><OverviewTab rfi={rfi} onReloaded={load} projectId={projectId} /></TabsContent>
          <TabsContent value="discussion"   className="mt-3"><DiscussionTab rfi={rfi} onReloaded={load} projectId={projectId} /></TabsContent>
          <TabsContent value="impact"       className="mt-3"><ImpactTab rfi={rfi} onReloaded={load} projectId={projectId} /></TabsContent>
          <TabsContent value="distribution" className="mt-3"><DistributionTab rfi={rfi} onReloaded={load} projectId={projectId} /></TabsContent>
          <TabsContent value="links"        className="mt-3"><LinksTab rfi={rfi} onReloaded={load} projectId={projectId} /></TabsContent>
          <TabsContent value="audit"        className="mt-3"><AuditTab rfi={rfi} /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════
function OverviewTab({ rfi, onReloaded, projectId }) {
  const [respBody, setRespBody] = useState('');
  const respond = async () => {
    if (!respBody.trim()) return;
    try {
      await api.post(`/edm/projects/${projectId}/rfis/${rfi.id}/respond`, { response: respBody, status: 'RESPONDED' });
      toast.success('RFI responded');
      setRespBody('');
      onReloaded();
    } catch { toast.error('Response failed'); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className={`${surfacePanel} p-4 lg:col-span-2 space-y-3`}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Question / Description</div>
          <div className={`text-sm ${bodyTxt} whitespace-pre-wrap`}>{rfi.description || <em className={mutedTxt}>No description provided.</em>}</div>
        </div>
        <div className="border-t border-slate-200 dark:border-slate-700/40 pt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Response</div>
          {rfi.response ? (
            <div>
              <div className={`text-sm ${bodyTxt} whitespace-pre-wrap`}>{rfi.response}</div>
              <div className={`text-[11px] ${mutedTxt} mt-1`}>Responded by <span className="font-mono">{rfi.response_by}</span> on {fmtDate(rfi.response_at)}</div>
            </div>
          ) : (
            <div className="space-y-2">
              <Textarea rows={4} value={respBody} onChange={(e) => setRespBody(e.target.value)} placeholder="Type your formal RFI response…" data-testid="rfi-response-body" />
              <Button className="bg-emerald-600 text-white" size="sm" onClick={respond} disabled={!respBody.trim()} data-testid="rfi-response-submit">
                <Send className="w-3.5 h-3.5 mr-1" /> Send Response
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className={`${surfacePanel} p-4 space-y-3 text-xs`}>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Meta</div>
        <MetaRow k="Status"       v={rfi.status?.replace(/_/g, ' ')} />
        <MetaRow k="Assigned to"  v={rfi.assigned_to || '—'} />
        <MetaRow k="Response due" v={fmtDate(rfi.response_due) || '—'} />
        <MetaRow k="Discipline"   v={rfi.discipline} />
        <MetaRow k="Priority"     v={rfi.priority} />
        <MetaRow k="Created"      v={`${fmtDate(rfi.created_at)} · ${rfi.created_by}`} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: DISCUSSION
// ═══════════════════════════════════════════════════════════════════════════
function DiscussionTab({ rfi, onReloaded, projectId }) {
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [busy, setBusy] = useState(false);
  const comments = rfi.comments || [];

  const post = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/edm/projects/${projectId}/rfis/${rfi.id}/comments`, { body, visibility });
      setBody(''); onReloaded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className={`${surfacePanel} p-4 space-y-3`}>
      <div className={`text-xs ${mutedTxt}`}>Threaded discussion — supports <b>@email</b> mentions and <b>public / internal</b> visibility.</div>

      <div className="space-y-2 max-h-[440px] overflow-auto pr-1" data-testid="rfi-comments">
        {comments.length === 0 && <div className={`text-xs ${mutedTxt} p-6 text-center`}>No comments yet. Start the thread below.</div>}
        {comments.map((c) => (
          <div key={c.id} className={`${subPanel} p-3 ${c.visibility === 'internal' ? 'border-l-4 border-l-amber-500' : ''}`} data-testid={`rfi-comment-${c.id}`}>
            <div className="flex items-center gap-2 text-[11px] mb-1">
              <span className="font-mono text-slate-500">{c.author}</span>
              <span className={mutedTxt}>·</span>
              <span className={mutedTxt}>{fmtDate(c.at)}</span>
              <span className={`ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded flex items-center gap-1
                ${c.visibility === 'internal' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'}`}>
                {c.visibility === 'internal' ? <><Lock className="w-2.5 h-2.5" /> Internal</> : <><Globe className="w-2.5 h-2.5" /> Public</>}
              </span>
              {(c.mentions || []).length > 0 && (
                <span className={`text-[9px] ${mutedTxt}`}>· mentions: {c.mentions.join(', ')}</span>
              )}
            </div>
            <div className={`text-xs ${bodyTxt} whitespace-pre-wrap`}>{c.body}</div>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-200 dark:border-slate-700/40 pt-3 space-y-2">
        <Label className="text-xs">Add comment</Label>
        <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Type your comment. Use @email@domain.com to mention teammates."
          data-testid="rfi-comment-input" />
        <div className="flex items-center gap-2">
          <Select value={visibility} onValueChange={setVisibility}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="rfi-comment-visibility"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Public (all parties)</SelectItem>
              <SelectItem value="internal">Internal (owner-side only)</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" className="bg-[#1E4E8C] text-white ml-auto" onClick={post} disabled={busy || !body.trim()} data-testid="rfi-comment-post">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Post
          </Button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: IMPACT
// ═══════════════════════════════════════════════════════════════════════════
function ImpactTab({ rfi, onReloaded, projectId }) {
  const [cost, setCost] = useState(rfi.cost_impact ?? 0);
  const [currency, setCurrency] = useState(rfi.cost_currency || 'USD');
  const [days, setDays] = useState(rfi.schedule_impact_days ?? 0);
  const [disp, setDisp] = useState(rfi.disposition || 'NONE');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/edm/projects/${projectId}/rfis/${rfi.id}/impact`, {
        cost_impact: Number(cost) || 0,
        currency, schedule_impact_days: Number(days) || 0,
        disposition: disp,
      });
      toast.success('Impact saved');
      onReloaded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className={`${surfacePanel} p-4 space-y-3 max-w-xl`}>
      <div className={`text-xs ${mutedTxt}`}>Log the commercial & schedule impact of this RFI so it rolls up into the RAID / CBS / EVM modules.</div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <Label className="text-xs">Cost impact</Label>
          <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="rfi-impact-cost" />
        </div>
        <div>
          <Label className="text-xs">Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="h-9 text-xs" data-testid="rfi-impact-currency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['USD','EUR','GBP','INR','AED','SAR','AUD','JPY'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs">Schedule impact (days)</Label>
        <Input type="number" value={days} onChange={(e) => setDays(e.target.value)} data-testid="rfi-impact-days" />
      </div>
      <div>
        <Label className="text-xs">Disposition</Label>
        <Select value={disp} onValueChange={setDisp}>
          <SelectTrigger className="h-9 text-xs" data-testid="rfi-impact-disposition"><SelectValue /></SelectTrigger>
          <SelectContent>
            {['NONE','ACCEPTED','REJECTED','SUPERSEDED','DEFERRED'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button className="bg-[#1E4E8C] text-white" onClick={save} disabled={busy} data-testid="rfi-impact-save">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Save Impact
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: DISTRIBUTION
// ═══════════════════════════════════════════════════════════════════════════
function DistributionTab({ rfi, onReloaded, projectId }) {
  const [dist, setDist] = useState((rfi.distribution || []).join(', '));
  const [watch, setWatch] = useState((rfi.watchers || []).join(', '));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/edm/projects/${projectId}/rfis/${rfi.id}/distribution`, {
        distribution: dist.split(/[,;]/).map((e) => e.trim()).filter(Boolean),
        watchers: watch.split(/[,;]/).map((e) => e.trim()).filter(Boolean),
      });
      toast.success('Distribution updated');
      onReloaded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className={`${surfacePanel} p-4 space-y-3 max-w-2xl`}>
      <div className={`text-xs ${mutedTxt}`}>Watchers are auto-notified on any update. Distribution list is copied on outgoing RFI transmittals.</div>
      <div>
        <Label className="text-xs">Distribution (comma-separated emails)</Label>
        <Input value={dist} onChange={(e) => setDist(e.target.value)} placeholder="cm@site.com, lead@epc.com" data-testid="rfi-dist-list" />
      </div>
      <div>
        <Label className="text-xs">Watchers (comma-separated emails)</Label>
        <Input value={watch} onChange={(e) => setWatch(e.target.value)} placeholder="pm@owner.com" data-testid="rfi-dist-watchers" />
      </div>
      <Button className="bg-[#1E4E8C] text-white" onClick={save} disabled={busy} data-testid="rfi-dist-save">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5 mr-1" />} Save
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: LINKS
// ═══════════════════════════════════════════════════════════════════════════
function LinksTab({ rfi, onReloaded, projectId }) {
  const linked = rfi.linked || { tasks: [], drawings: [], submittals: [], documents: [] };
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [docMap, setDocMap] = useState({});   // { id: {doc_number,title,discipline,revision} }

  // Resolve linked document IDs into human-readable rows
  useEffect(() => {
    const ids = linked.documents || [];
    if (ids.length === 0) { setDocMap({}); return; }
    (async () => {
      try {
        const r = await api.get(`/edm/projects/${projectId}/documents`);
        const byId = {};
        for (const d of (r.data?.documents || [])) {
          if (ids.includes(d.document_id)) {
            byId[d.document_id] = { ...d, id: d.document_id,
              doc_number: d.document_no, revision: d.latestRevision?.revision_code };
          }
        }
        setDocMap(byId);
      } catch { setDocMap({}); }
    })();
  }, [projectId, JSON.stringify(linked.documents || [])]);

  const saveLinks = async (nextDocs) => {
    setBusy(true);
    try {
      await api.put(`/edm/projects/${projectId}/rfis/${rfi.id}/links`, {
        tasks:      linked.tasks || [],
        drawings:   linked.drawings || [],
        submittals: linked.submittals || [],
        documents:  nextDocs,
      });
      toast.success('Links updated');
      onReloaded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const onPick = (docs) => {
    const ids = docs.map((d) => d.id);
    const merged = Array.from(new Set([...(linked.documents || []), ...ids]));
    saveLinks(merged);
  };

  const removeDoc = (id) => saveLinks((linked.documents || []).filter((x) => x !== id));

  return (
    <div className={`${surfacePanel} p-4 space-y-3`}>
      <div className="flex items-center justify-between">
        <div className={`text-xs ${mutedTxt}`}>Cross-link this RFI to schedule activities, drawings, submittals and documents.</div>
        <Button size="sm" variant="outline" onClick={() => setShowPicker(true)} disabled={busy} data-testid="rfi-links-attach-docs">
          <Link2 className="w-3.5 h-3.5 mr-1" /> Attach Documents
        </Button>
      </div>

      <div className={`${subPanel} p-3`}>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Documents ({(linked.documents || []).length})</div>
        {(linked.documents || []).length === 0 ? (
          <div className={`text-[11px] ${mutedTxt}`}>None linked.</div>
        ) : (
          <div className="space-y-1">
            {(linked.documents || []).map((id) => {
              const d = docMap[id];
              return (
                <div key={id} className="text-xs flex items-center gap-2 border border-slate-200 dark:border-slate-700/40 rounded px-2 py-1" data-testid={`rfi-linked-doc-${id}`}>
                  <span className="font-mono text-slate-500 w-32 truncate">{d?.doc_number || `${id.slice(0, 8)}…`}</span>
                  <span className={`flex-1 truncate ${bodyTxt}`}>{d?.title || <em className={mutedTxt}>unresolved document</em>}</span>
                  {d?.discipline && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">{d.discipline}</span>}
                  {d?.revision && <span className="text-[10px] font-mono text-slate-400">Rev {d.revision}</span>}
                  <button onClick={() => removeDoc(id)} className="text-red-500 hover:text-red-600" data-testid={`rfi-link-remove-${id}`}><X className="w-3 h-3" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <LinkBlock title="Tasks / Activities" items={linked.tasks || []}     />
      <LinkBlock title="Drawings"    items={linked.drawings || []}    />
      <LinkBlock title="Submittals"  items={linked.submittals || []}  />

      <DocumentPickerDialog
        open={showPicker}
        projectId={projectId}
        onClose={() => setShowPicker(false)}
        onPick={onPick}
        title="Link documents to RFI"
      />
    </div>
  );
}

function LinkBlock({ title, items, onRemove }) {
  return (
    <div className={`${subPanel} p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{title} ({items.length})</div>
      {items.length === 0 ? (
        <div className={`text-[11px] ${mutedTxt}`}>None linked.</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {items.map((id) => (
            <span key={id} className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300 font-mono flex items-center gap-1">
              {id.slice(0, 8)}…
              {onRemove && <button onClick={() => onRemove(id)} className="ml-0.5 hover:text-red-500" aria-label="remove" data-testid={`rfi-link-remove-${id}`}><X className="w-2.5 h-2.5" /></button>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: AUDIT
// ═══════════════════════════════════════════════════════════════════════════
function AuditTab({ rfi }) {
  const audit = rfi.audit_trail || [];
  return (
    <div className={`${surfacePanel} p-4`}>
      <div className={`text-xs ${mutedTxt} mb-3`}>Every action on this RFI is recorded here for compliance.</div>
      {audit.length === 0 ? (
        <div className={`text-xs ${mutedTxt} p-6 text-center`}>No audit events yet.</div>
      ) : (
        <div className="space-y-1">
          {audit.map((a) => (
            <div key={a.id} className={`${subPanel} p-2 flex items-start gap-3 text-xs`} data-testid={`rfi-audit-${a.id}`}>
              <div className="w-2 h-2 rounded-full bg-fuchsia-500 mt-1.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-slate-500">{a.actor}</span>
                  <span className={mutedTxt}>·</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 ${bodyTxt}`}>{a.action}</span>
                  <span className={`${mutedTxt} ml-auto text-[10px]`}>{fmtDate(a.at)}</span>
                </div>
                {a.details && Object.keys(a.details).length > 0 && (
                  <pre className={`${mutedTxt} text-[10px] mt-1 whitespace-pre-wrap`}>{JSON.stringify(a.details, null, 0)}</pre>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════
function KPI({ icon: Icon, label, value, tone }) {
  const toneMap = {
    sky: 'text-sky-600 dark:text-sky-300',
    amber: 'text-amber-600 dark:text-amber-300',
    fuchsia: 'text-fuchsia-600 dark:text-fuchsia-300',
    emerald: 'text-emerald-600 dark:text-emerald-300',
    red: 'text-red-600 dark:text-red-300',
  };
  return (
    <div className={`${subPanel} p-3`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${toneMap[tone]}`} />
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      </div>
      <div className={`text-lg font-semibold mt-1 ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}

function MetaRow({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/40 pb-1">
      <span className="text-slate-500 text-[10px] uppercase tracking-wider">{k}</span>
      <span className={`${bodyTxt} text-right truncate max-w-[60%]`}>{v ?? '—'}</span>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

function fmtCurrency(n, cur) {
  const val = Number(n || 0);
  const c = cur || 'USD';
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(val); }
  catch { return `${c} ${val}`; }
}
